<template>
  <div
    class="candidate signup-progress-bar"
    :class="{ 'fixed-bottom': fixedBottom }"
    :style="styleObject"
    v-scroll="onScroll"
  >
    <div class="progress-bar-container pb-3">
      <ol class="progress-bar">
        <li
          v-for="(step, i) in sortedSteps"
          :key="`step-${i}`"
          :class="{
            active: step.show && (!sortedSteps[i + 1] || !sortedSteps[i + 1].show),
            complete: done,
          }"
          class="mdi"
        >
          <span> You are here </span>
          <span class="subscript" :class="themes.candidate.light.mutedText"> {{ step.name }} </span>
        </li>
      </ol>
    </div>
    <div class="text-xs-center">
      <v-chip small :color="color" class="white--text">
        <span class="font-weight-bold" v-if="timeRemaining > 0">
          &plusmn; {{ timeRemaining }} min remaining
        </span>
        <span class="font-weight-bold" v-else>&lt; 1 min remaining</span>
      </v-chip>
    </div>
  </div>
</template>

<script>
// Helpers
import { mapGetters } from 'vuex'

// Mixins
import { colorStringToHex } from '@/mixins/colorStringToHex'

// Data
import signupCopy from '@/data/signup_copy'

export default {
  mixins: [colorStringToHex],

  created() {
    this.onScroll()
  },

  mounted() {
    if (this.campaign && signupCopy[this.campaignOrganization.id]) {
      this.color = signupCopy[this.campaignOrganization.id].color
    }
  },

  props: {
    steps: Object,
  },

  data() {
    return {
      fixedBottom: false,
      color: signupCopy.standard.color,
    }
  },

  computed: {
    ...mapGetters({
      campaign: 'campaign/model',
      campaignOrganization: 'campaign/organization',
    }),
    styleObject() {
      return {
        '--color-white': this.colorStringToHex('white'),
        '--color-grey': this.colorStringToHex(this.themes.candidate.light.muted),
        '--color-progress': this.color,
        '--color-bg': this.colorStringToHex('grey lighten-5'),
      }
    },

    sortedSteps() {
      let sortedSteps = JSON.parse(JSON.stringify(this.steps))
      sortedSteps = Object.keys(sortedSteps)
        .sort((a, b) => sortedSteps[a].order - sortedSteps[b].order)
        .map(step => sortedSteps[step])
      sortedSteps.pop() // final steps needs no action

      return sortedSteps
    },

    timeRemaining() {
      const steps = JSON.parse(JSON.stringify(this.sortedSteps))
      return steps.reduce((time, step) => {
        return step.show ? time : time + step.duration
      }, 0)
    },

    done() {
      return !Object.keys(this.steps).some(step => !this.steps[step].show)
    },
  },

  methods: {
    onScroll() {
      const location =
        document.documentElement.offsetHeight -
        (window.pageYOffset || document.documentElement.scrollTop)
      const toBottom = this.$vuetify.breakpoint.height - location
      this.fixedBottom = toBottom < 0
    },
  },
}
</script>

<style lang="scss">
.candidate.signup-progress-bar {
  position: fixed;
  left: 0;
  bottom: 0rem;
  height: 9.2rem;
  width: 100%;
  left: 50%;
  transform: translateX(-50%);
  z-index: 1;
  transition: bottom 0.4s ease-out;

  @media (min-width: 600px) {
    position: absolute;
  }

  &:before {
    content: '';
    position: fixed;
    height: 10rem;
    width: 100%;
    bottom: 0;
    left: 0;
    background: var(--color-bg);

    @media (min-width: 600px) {
      position: absolute;
    }
  }

  &.fixed-bottom {
    bottom: 0;
  }

  .progress-bar-container {
    position: relative;
    width: 90%;
    max-width: 300px;
    margin: auto;

    @media (min-width: 600px) {
      width: 73%;
    }

    @media (min-width: 960px) {
      width: 50%;
    }

    @media (min-width: 1264px) {
      width: 55%;
    }

    @media (min-width: 1904px) {
      padding-left: 1rem;
      width: 47%;
    }

    .progress-bar {
      display: flex;
      justify-content: space-between;
      list-style: none;
      padding: 0;
      margin: 0 0 1.5rem 0;

      li {
        flex: 2;
        position: relative;
        padding: 0 0 1.5rem 0;
        line-height: 1.5;
        white-space: nowrap;
        overflow: visible;
        min-width: 0;
        text-align: center;
        color: var(--color-progress);
        border-bottom: 7px solid var(--color-grey);

        &:not(:first-child):not(:last-child) span {
          position: absolute;
          top: 0;
          left: 50%;
          transform: translateX(-50%);
        }

        &:before {
          // mdi-check
          content: '\F012C';
          display: block;
          width: 36px;
          height: 36px;
          background-color: var(--color-progress);
          border-radius: 50%;
          border: 4px solid var(--color-bg);
          position: absolute;
          left: calc(50% - 20px);
          bottom: -21px;
          z-index: 3;
          color: var(--color-white);
          font-size: 18px;
          font-weight: 700;
          text-align: center;
          transition: all 0.4s ease-in-out;
        }

        &.active:not(.complete):before,
        &.visible:before {
          // mdi-pencil
          content: '\F03EB';
          // content: var(--icon-active);
          background-color: var(--color-white);
          border-color: var(--color-progress);
          width: 42px;
          height: 42px;
          bottom: -24px;
          color: var(--color-progress);
          font-size: 22px;
          font-weight: 700;
          text-align: center;
          z-index: 4;
        }

        &:first-child {
          text-align: left;
          flex: 1;
        }

        &:first-child.active:not(.complete):before,
        &:first-child:before {
          left: -1px;
        }

        &:last-child {
          text-align: right;
          flex: 1;
        }

        &:last-child span {
          width: 200%;
          display: inline-block;
          position: absolute;
          left: -100%;
        }

        &:last-child:before {
          right: -1px;
          left: auto;
        }

        &.complete > span,
        &:not(.active):not(.visible) > span:not(.subscript) {
          opacity: 0;
        }

        &:not(:first-child):after,
        &.active:not(:first-child):after {
          content: '';
          display: block;
          width: 100%;
          position: absolute;
          bottom: -7px;
          left: -50%;
          z-index: 2;
          border-bottom: 7px solid var(--color-progress);
        }

        &.active:last-child:after {
          width: 200%;
          left: -100%;
        }

        &.active ~ li:before {
          //mdi-pencil-outline
          content: '\F03EB';
          border-color: var(--color-bg);
          background-color: var(--color-grey);
          font-weight: 500;
        }

        &:first-child > span.subscript {
          text-align: left;
          left: 0;
        }

        &:last-child > span.subscript {
          width: 200%;
          text-align: right;
        }

        &:hover span.subscript,
        span.subscript {
          opacity: 1;
          position: absolute;
          margin-top: 4.9rem;
          font-size: 11px;
        }
      }

      span {
        transition: opacity 0.4s ease-in-out;
      }

      .complete:last-child:after,
      .active:last-child:after {
        width: 200%;
        left: -100%;
      }

      .active ~ li,
      .active ~ li:after {
        border-color: var(--color-grey);
      }
    }
  }
}
</style>
